import { useEffect } from "react";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import styled from "styled-components";
import TableView, { ColumnConfig, FetchMoreData, FetchParams } from "../../components/controls/table-view";
import Button from "../../components/controls/button";
import { TextContainer } from "../../styles/shared/table";
import { openDrawer } from "../../store/features/drawer/drawer-slice";
import { DrawerID } from "../../constants/drawer-constants";
import {
    GoogleIcon,
    ICON_SIZES,
    ICON_ID,
} from "../../components/icons/google-icon";
import useScreenSize from "../../hooks/useScreenSize";
import {
    resetSelectedProduct,
    fetchProductById,
    resetProducts,
    setSelectedDepartment,
    fetchProductsBySubDepartment,
    updateProductOrder,
    setSelectedSubDepartment,
    deleteProduct,
    removeProduct
} from "../../store/features/product/admin-product-slice";
import {
    ProductContainer,
    ProductDescription,
    ProductImage,
    ProductNameContainer,
    ProductTitle,
} from "../../styles/shared/product";
import Image from "../../components/controls/image";
import { fetchBranches } from "../../store/features/organisation/branch-slice";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { setCurrentPage } from "../../store/features/authentication/user-slice";
import AdminSidebar from "../../components/layout/admin-sidebar";
import { fetchDepartments, fetchSubDepartments } from "../../store/features/organisation/department-slice";
import { ApplicationVAT } from "../../models/finance/application-vat";
import { ApplicationSubDepartment } from "../../models/organisation/application-sub-department";
import { ButtonTabs } from "../../components/controls/button-tabs";
import EditableField from "../../components/controls/editable-field";
import { openModal } from "../../store/features/modal/modal-slice";
import { ModalID } from "../../constants/modal-constants";
import ConfirmModal from "../../components/notification/confirm-modal";
import { closeConfirm, openConfirm } from "../../store/features/notification/confirm-slice";
import { showSuccess } from "../../components/notification/toastr-actions";
export default function AdminAllProductView() {
    const { isMobile } = useScreenSize();
    const productState = useAppSelector((state) => state.adminProduct);
    const dispatch = useAppDispatch();
    const departments = useAppSelector((state) => state.department);

    useEffect(() => {
        dispatch(fetchBranches());
        dispatch(fetchDepartments()).then((action) => {
            if (
                fetchDepartments.fulfilled.match(action) &&
                action.payload.departments &&
                action.payload.departments.length > 0
                && productState.selectedDepartment == null
            ) {
                const firstDepartment = action.payload.departments[0];
                if (firstDepartment) {
                    handleDepartmentSelect(firstDepartment);
                }
            }
        });
        dispatch(setCurrentPage("Product List"));

        return () => {
            dispatch(resetProducts());
            dispatch(setSelectedDepartment(null));
            dispatch(setSelectedSubDepartment(null));
        }
    }, [dispatch]);

    const handleDepartmentSelect = async (department: ApplicationDepartment) => {
        dispatch(setSelectedDepartment(department));
        if (productState.selectedDepartment?.id !== department.id) {
            dispatch(fetchSubDepartments(department.id)).then((action) => {
                if (
                    fetchSubDepartments.fulfilled.match(action) &&
                    action.payload &&
                    action.payload.subDepartments.length > 0) {
                    const firstSubDepartment = action.payload.subDepartments[0];
                    if (firstSubDepartment) {
                        handleSubDepartmentClick(firstSubDepartment);
                    }
                }
            });

        }
    };

    const handleSubDepartmentClick = async (subDepartment: ApplicationSubDepartment) => {
        dispatch(setSelectedSubDepartment(subDepartment));
        if (productState.selectedSubDepartment?.id !== subDepartment.id) {
            await dispatch(fetchProductsBySubDepartment({ subDepartmentId: subDepartment.id, fetchParams: { reset: true } }))
        }
    };

    const fetchMoreData = async (fetchParams: FetchParams) => {
        if (productState.selectedSubDepartment != null) {
            await dispatch(fetchProductsBySubDepartment({
                subDepartmentId: productState.selectedSubDepartment.id,
                fetchParams,
            }));
        }
    };

    const renderActionButton = (row: any) => (
        <Button
            variant={isMobile ? "text" : "outlined"}
            id={row.Name}
            label={isMobile ? "" : "Edit"}
            icon={isMobile ? ICON_ID.Edit : undefined}
            isPrimary={true}
            fullWidth={false}
            onClick={() => EditProduct(row)}
        />
    );

    const openCreateNewProduct = () => {
        dispatch(resetSelectedProduct());
        dispatch(
            openDrawer({
                id: DrawerID.AdminProduct,
                anchor: "right",
                data: {
                    headerText: "Create New Product",
                    departmentId: productState.selectedDepartment!.id!,
                    subDepartmentId: productState.selectedSubDepartment!.id!
                },
            })
        );
    };
    const EditProduct = async (row: any) => {
        const response = await dispatch(fetchProductById(row.id));
        if (response) {
            dispatch(
                openDrawer({
                    id: DrawerID.AdminProduct,
                    anchor: "right",
                    data: {
                        headerText: `Edit ${row.name}`,
                    },
                })
            );
        }
    };

    const columnConfig: { [key: string]: ColumnConfig } = {
        id: {
            hidden: true,
        },
        image: {
            hidden: true,
        },
        department: {
            hidden: true
        },
        name: {
            order: 1,
            name: "Product",
            colspan: 2,
            render: (value: string, row: any) => (
                <ProductContainer>
                    <ProductImage>
                        {row.image ? (
                            <Image image={row.image}></Image>
                        ) : (
                            <></>
                        )}
                    </ProductImage>
                    <ProductNameContainer>
                        <ProductTitle>
                            {value}

                        </ProductTitle>
                        <ProductDescription>
                            {row.description}
                        </ProductDescription>
                    </ProductNameContainer>
                </ProductContainer>
            ),
        },
        plu: {
            order: 2,
        },
        subDepartment: {
            order: 3,
            name: "Sub department",
            render: (value: ApplicationSubDepartment) => (
                <TextContainer>{value.name}</TextContainer>
            )
        },
        vat: {
            order: 4,
            render: (value: ApplicationVAT) => (
                <TextContainer>{value.description}</TextContainer>
            )
        },
        active: {
            hideOnMobile: true,
            order: 5,
            render: (value: boolean) => (
                <TextContainer>
                    {value ? (
                        <GoogleIcon
                            tooltip="Active"
                            id={ICON_ID.CheckCircle}
                            size={ICON_SIZES.Large}
                            isSuccess
                        />
                    ) : (
                        <GoogleIcon
                            tooltip="Disabled"
                            id={ICON_ID.Close}
                            size={ICON_SIZES.Large}
                            isDanger
                        />
                    )}
                </TextContainer>
            ),
        },
        order: {
            order: 6,
            render: (value: number, row: any) => (
                <EditableField
                    type="number"
                    value={value}
                    label="Order"
                    displayValue={value.toString()}
                    onSave={(savedValue) => dispatch(updateProductOrder({ id: row.id, field: "order", value: savedValue }))}
                />
            ),
        },
        canDelete: {
            order: 7,
            name: "",
            render: (value: boolean, row: any) => {
                return value ? (
                    <Button
                        id={`btnDelete${row.id}`}
                        label="Delete"
                        variant="outlined"
                        onClick={() => handleDeleteProduct(row)}
                        isDanger
                    />
                ) : null;
            }
        }
    }

    const renderToolbar = () => (
        <>
            <Button
                id="createNewProduct"
                onClick={openCreateNewProduct}
                label="New Product"
                variant="contained"
                icon={ICON_ID.Add}
            ></Button>

            <Button id="btnOpenBulkUpload" variant="outlined" label="Upload CSV" onClick={() => dispatch(openModal({
                id: ModalID.UploadProduct,
                data: {
                    departmentId: productState.selectedDepartment!.id,
                    subDepartmentId: productState.selectedSubDepartment!.id
                }
            }))} />
        </>
    )

    const handleDeleteProduct = (row: any) => {
        const onConfirm = async () => {
            const response = await dispatch(deleteProduct(row.id));
            if (response) {
                dispatch(removeProduct(row.id));
                dispatch(closeConfirm());
                dispatch(showSuccess(`${row.name} deleted successfully.`));
            }
        };
        const onCancel = () => {
            dispatch(closeConfirm());
        };
        dispatch(
            openConfirm({
                title: "Remove " + row.name + "?",
                message: "Are you sure you want to delete this product?",
                show: true,
                data: {
                    onConfirm: onConfirm,
                    onCancel: onCancel,
                },
            })
        );
    };
    return (
        <Container>
            <NavigationSection>
                <AdminSidebar
                    items={(departments.departments?.departments ?? []).map((department) => ({
                        label: department.name,
                        action: () => handleDepartmentSelect(department),
                        isSelected: department.id === productState.selectedDepartment?.id
                    }))}
                ></AdminSidebar>

            </NavigationSection>
            <ContentSection>
                <ContentTitle>
                    <FieldsGridContainer>
                        <ProductDepartmentTitle>
                            {productState.selectedDepartment?.name || ""}
                        </ProductDepartmentTitle>
                    </FieldsGridContainer>
                </ContentTitle>
                <ButtonTabs
                    items={(departments.subDepartments?.subDepartments ?? []).map((subDepartment) => ({
                        label: subDepartment.name,
                        onClick: () => handleSubDepartmentClick(subDepartment),
                        isSelected: productState.selectedSubDepartment?.id === subDepartment.id
                    }))}
                ></ButtonTabs>
                <Body>
                    {productState.selectedDepartment && productState.selectedSubDepartment && (
                        <TableContainer>

                            <TableView
                                id="tblProducts"
                                emptyText="No products"
                                toolbar={renderToolbar}
                                fetchData={(fetchParams) => fetchMoreData(fetchParams)}
                                totalCount={productState.products?.count ?? 0}
                                data={productState?.products?.products ?? []}
                                columnConfig={columnConfig}
                                actionButton={renderActionButton}
                                showHeaders={true}
                                preLoadedData={true}
                                search={true}
                            />
                        </TableContainer>
                    )}
                </Body>
            </ContentSection>
        </Container>
    );
}

const Body = styled.div`
  height: 100%;
`;


const Container = styled.div`
  display: flex;
  margin: 10px;
  column-gap: 20px;
  height: 100%;
  @media (max-width: 1600px) {
    flex-direction: column;
  }
`;

const ProductDepartmentTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
`;

const ContentTitle = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  border-bottom: 2px solid ${(props) => props.theme.application.scheme.primary};
  padding: 5px 0px 5px 5px;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px;
  background: ${(props) => props.theme.application.background};
  border-radius: 4px;
  height: 100%;
`;
const FieldsGridContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const TableContainer = styled.div`
  padding: 5px;
  height: 95%;
  @media (max-width: 700px) {
    padding: 0px;
  }
`;

const NavigationSection = styled.div`
  min-width: 15%;
`;
